import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Plus, Briefcase, Building2, Upload, Loader2, Trash2, Eye, Edit3, X, Phone, Download, Check, Mail, FileText } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError, fileUrl, API } from "@/lib/api";
import SEO from "@/components/SEO";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import VerifiedBadge from "@/components/VerifiedBadge";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { CITIES, WORK_TYPES } from "@/data/categories";

const EMPTY = { title: "", category_slug: "", location: "", work_type: "Tam Zamanlı", experience_level: "Fark etmez", salary: "", description: "", requirements: "", skills: [] };

export default function EmployerDashboard() {
  const { user, refreshUser } = useAuth();
  const [params, setParams] = useSearchParams();
  const [tab, setTab] = useState(params.get("tab") || "jobs");
  const [cats, setCats] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [skillInput, setSkillInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [company, setCompany] = useState({ company_name: "", about: "", website: "", location: "", sector: "" });
  const [uploading, setUploading] = useState(false);
  const [apps, setApps] = useState([]);

  const loadJobs = () => api.get("/my/jobs").then((r) => setJobs(r.data));
  const loadApps = () => api.get("/employer/applications").then((r) => setApps(r.data)).catch(() => {});

  const setAppStatus = async (id, status) => {
    await api.put(`/applications/${id}/status?status=${status}`);
    toast.success(status === "approved" ? "Başvuru onaylandı" : "Başvuru reddedildi");
    loadApps();
  };

  useEffect(() => {
    api.get("/categories").then((r) => setCats(r.data));
    loadJobs();
    loadApps();
    if (user?.company) setCompany({
      company_name: user.company.company_name || "", about: user.company.about || "",
      website: user.company.website || "", location: user.company.location || "", sector: user.company.sector || "",
    });
  }, [user]);

  const changeTab = (t) => { setTab(t); setParams({ tab: t }); };

  const addSkill = () => {
    const s = skillInput.trim();
    if (s && !form.skills.includes(s)) { setForm((f) => ({ ...f, skills: [...f.skills, s] })); setSkillInput(""); }
  };

  const submitJob = async (e) => {
    e.preventDefault();
    if (!form.category_slug) { toast.error("Kategori seçin"); return; }
    setBusy(true);
    try {
      if (editId) {
        await api.put(`/jobs/${editId}`, form);
        toast.success("İlan güncellendi");
      } else {
        await api.post("/jobs", form);
        toast.success("İlan yayınlandı ve indexlendi!");
      }
      setForm(EMPTY); setEditId(null); changeTab("jobs"); loadJobs();
    } catch (e2) { toast.error(formatApiError(e2.response?.data?.detail)); } finally { setBusy(false); }
  };

  const editJob = (j) => {
    setForm({ title: j.title, category_slug: j.category_slug, location: j.location, work_type: j.work_type, experience_level: j.experience_level || "Fark etmez", salary: j.salary || "", description: j.description, requirements: j.requirements || "", skills: j.skills || [] });
    setEditId(j.id); changeTab("new");
  };

  const delJob = async (id) => {
    await api.delete(`/jobs/${id}`);
    toast.success("İlan silindi"); loadJobs();
  };

  const saveCompany = async () => {
    await api.put("/company", company);
    await refreshUser();
    toast.success("Şirket bilgileri kaydedildi");
  };

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      await api.post("/upload/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await refreshUser();
      toast.success("Logo yüklendi");
    } catch (e2) { toast.error(formatApiError(e2.response?.data?.detail)); } finally { setUploading(false); }
  };

  const verified = user?.company?.verified;

  return (
    <div className="min-h-screen bg-white">
      <SEO title="İşveren Paneli | isbul.org" description="İlanlarınızı yönetin ve yeni ilan yayınlayın." canonical="/isveren" />
      <Header />
      <div className="max-w-6xl mx-auto px-4 lg:px-8 py-10">
        <div className="flex items-center gap-2 flex-wrap">
          <h1 className="text-2xl sm:text-3xl font-extrabold text-[#1f222d]">{company.company_name || user?.name}</h1>
          {verified ? <VerifiedBadge showText /> : <span className="text-xs bg-amber-50 text-amber-700 border border-amber-200 rounded-full px-2 py-0.5">Onay bekliyor</span>}
        </div>
        <p className="text-slate-500 mt-1">İşveren paneli — ilanlarınızı yönetin.</p>

        <div className="flex gap-2 mt-6 border-b border-slate-200">
          {[["jobs", "İlanlarım"], ["applications", `Başvurular (${apps.length})`], ["new", editId ? "İlanı Düzenle" : "Yeni İlan"], ["company", "Şirket Profili"]].map(([k, label]) => (
            <button key={k} data-testid={`emp-tab-${k}`} onClick={() => { if (k === "new" && tab !== "new") { setForm(EMPTY); setEditId(null); } changeTab(k); }} className={`px-4 py-3 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === k ? "border-blue-600 text-blue-600" : "border-transparent text-slate-500 hover:text-[#1f222d]"}`}>
              {label}
            </button>
          ))}
        </div>

        {/* Jobs list */}
        {tab === "jobs" && (
          <div className="mt-6">
            <div className="flex justify-between items-center mb-4">
              <p className="text-sm text-slate-500">{jobs.length} ilan</p>
              <Button data-testid="emp-new-job" onClick={() => { setForm(EMPTY); setEditId(null); changeTab("new"); }} className="bg-blue-600 hover:bg-blue-700 text-white rounded-full"><Plus size={16} className="mr-1" /> Yeni İlan</Button>
            </div>
            {jobs.length === 0 ? (
              <div className="text-center py-16 bg-slate-50 rounded-2xl border border-slate-100">
                <Briefcase size={36} className="text-slate-300 mx-auto mb-3" />
                <p className="text-[#1f222d] font-semibold">Henüz ilanınız yok</p>
                <p className="text-sm text-slate-500 mt-1">İlk ilanınızı yayınlayın, hemen indexlensin.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {jobs.map((j) => (
                  <div key={j.id} data-testid={`emp-job-${j.id}`} className="bg-white border border-slate-200 rounded-2xl p-5 flex items-center justify-between gap-4 flex-wrap">
                    <div className="min-w-0">
                      <h3 className="font-semibold text-[#1f222d]">{j.title}</h3>
                      <p className="text-sm text-slate-500 mt-0.5">{j.location} · {j.work_type} · {j.category_name}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="inline-flex items-center gap-1 text-xs text-slate-500"><Eye size={14} /> {j.views || 0}</span>
                      <span className={`text-xs rounded-full px-2 py-0.5 ${j.status === "approved" ? "bg-green-50 text-green-700 border border-green-200" : "bg-amber-50 text-amber-700 border border-amber-200"}`}>{j.status === "approved" ? "Yayında" : j.status}</span>
                      <button data-testid={`emp-edit-${j.id}`} onClick={() => editJob(j)} className="w-9 h-9 rounded-lg border border-slate-200 flex items-center justify-center hover:bg-slate-50"><Edit3 size={15} /></button>
                      <button data-testid={`emp-del-${j.id}`} onClick={() => delJob(j.id)} className="w-9 h-9 rounded-lg border border-red-200 text-red-600 flex items-center justify-center hover:bg-red-50"><Trash2 size={15} /></button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* New / edit job */}
        {tab === "new" && (
          <form onSubmit={submitJob} className="mt-6 bg-white border border-slate-200 rounded-2xl p-6 max-w-3xl">
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <label className="text-sm font-medium text-[#1f222d]">İlan Başlığı *</label>
                <input data-testid="job-title" required value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} className="w-full mt-1.5 border border-slate-200 rounded-xl px-4 py-2.5 outline-none focus:border-blue-400 text-[#1f222d]" placeholder="Örn: Senior Frontend Developer" />
              </div>
              <div>
                <label className="text-sm font-medium text-[#1f222d]">Kategori *</label>
                <select data-testid="job-category" required value={form.category_slug} onChange={(e) => setForm((f) => ({ ...f, category_slug: e.target.value }))} className="w-full mt-1.5 border border-slate-200 rounded-xl px-4 py-2.5 outline-none focus:border-blue-400 text-[#1f222d]">
                  <option value="">Seçiniz</option>{cats.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <label className="text-sm font-medium text-[#1f222d]">Şehir *</label>
                <select data-testid="job-location" required value={form.location} onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))} className="w-full mt-1.5 border border-slate-200 rounded-xl px-4 py-2.5 outline-none focus:border-blue-400 text-[#1f222d]">
                  <option value="">Seçiniz</option>{CITIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div>
                <label className="text-sm font-medium text-[#1f222d]">Çalışma Türü</label>
                <select data-testid="job-worktype" value={form.work_type} onChange={(e) => setForm((f) => ({ ...f, work_type: e.target.value }))} className="w-full mt-1.5 border border-slate-200 rounded-xl px-4 py-2.5 outline-none focus:border-blue-400 text-[#1f222d]">
                  {WORK_TYPES.map((w) => <option key={w} value={w}>{w}</option>)}
                </select>
              </div>
              <div>
                <label className="text-sm font-medium text-[#1f222d]">Maaş (opsiyonel)</label>
                <input data-testid="job-salary" value={form.salary} onChange={(e) => setForm((f) => ({ ...f, salary: e.target.value }))} className="w-full mt-1.5 border border-slate-200 rounded-xl px-4 py-2.5 outline-none focus:border-blue-400 text-[#1f222d]" placeholder="Örn: 40.000 - 60.000 TL" />
              </div>
            </div>

            <label className="text-sm font-medium text-[#1f222d] block mt-4">İş Tanımı *</label>
            <textarea data-testid="job-description" required value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} rows={4} className="w-full mt-1.5 border border-slate-200 rounded-xl px-4 py-2.5 outline-none focus:border-blue-400 text-[#1f222d] resize-none" placeholder="Pozisyonun sorumluluklarını açıklayın" />

            <label className="text-sm font-medium text-[#1f222d] block mt-4">Aranan Nitelikler</label>
            <textarea data-testid="job-requirements" value={form.requirements} onChange={(e) => setForm((f) => ({ ...f, requirements: e.target.value }))} rows={3} className="w-full mt-1.5 border border-slate-200 rounded-xl px-4 py-2.5 outline-none focus:border-blue-400 text-[#1f222d] resize-none" placeholder="Gerekli deneyim, eğitim, yetenekler" />

            <label className="text-sm font-medium text-[#1f222d] block mt-4">Yetenekler (AI eşleştirme için)</label>
            <div className="flex gap-2 mt-1.5">
              <input data-testid="job-skill-input" value={skillInput} onChange={(e) => setSkillInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addSkill())} className="flex-1 border border-slate-200 rounded-xl px-4 py-2.5 outline-none focus:border-blue-400 text-[#1f222d]" placeholder="Yetenek ekleyip Enter'a basın" />
              <Button type="button" data-testid="job-add-skill" onClick={addSkill} className="bg-blue-600 hover:bg-blue-700 text-white rounded-xl">Ekle</Button>
            </div>
            <div className="flex flex-wrap gap-2 mt-3">
              {form.skills.map((s) => (
                <span key={s} className="inline-flex items-center gap-1 bg-blue-50 text-blue-700 rounded-full px-3 py-1 text-sm">{s}
                  <button type="button" onClick={() => setForm((f) => ({ ...f, skills: f.skills.filter((x) => x !== s) }))}><X size={13} /></button>
                </span>
              ))}
            </div>

            <Button data-testid="job-submit" type="submit" disabled={busy} className="mt-6 bg-blue-600 hover:bg-blue-700 text-white rounded-xl px-8 py-6 font-semibold">
              {busy ? "Kaydediliyor..." : editId ? "İlanı Güncelle" : "İlanı Yayınla"}
            </Button>
          </form>
        )}

        {/* Company */}
        {tab === "company" && (
          <div className="mt-6 bg-white border border-slate-200 rounded-2xl p-6 max-w-2xl">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-20 h-20 rounded-2xl bg-slate-50 border border-slate-100 flex items-center justify-center overflow-hidden">
                {user?.company?.logo_file_id ? <img src={fileUrl(user.company.logo_file_id)} alt="logo" className="w-full h-full object-contain" /> : <Building2 size={28} className="text-blue-600" />}
              </div>
              <label data-testid="company-logo-upload" className="inline-flex items-center gap-2 border border-slate-200 rounded-xl px-4 py-2.5 cursor-pointer hover:border-blue-400 text-sm font-medium text-[#1f222d]">
                {uploading ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />} Logo Yükle
                <input type="file" accept="image/*" className="hidden" onChange={uploadLogo} />
              </label>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <label className="text-sm font-medium text-[#1f222d]">Şirket Adı</label>
                <input data-testid="company-name" value={company.company_name} onChange={(e) => setCompany((c) => ({ ...c, company_name: e.target.value }))} className="w-full mt-1.5 border border-slate-200 rounded-xl px-4 py-2.5 outline-none focus:border-blue-400 text-[#1f222d]" />
              </div>
              <div>
                <label className="text-sm font-medium text-[#1f222d]">Sektör</label>
                <input data-testid="company-sector" value={company.sector} onChange={(e) => setCompany((c) => ({ ...c, sector: e.target.value }))} className="w-full mt-1.5 border border-slate-200 rounded-xl px-4 py-2.5 outline-none focus:border-blue-400 text-[#1f222d]" />
              </div>
              <div>
                <label className="text-sm font-medium text-[#1f222d]">Şehir</label>
                <select data-testid="company-location" value={company.location} onChange={(e) => setCompany((c) => ({ ...c, location: e.target.value }))} className="w-full mt-1.5 border border-slate-200 rounded-xl px-4 py-2.5 outline-none focus:border-blue-400 text-[#1f222d]">
                  <option value="">Seçiniz</option>{CITIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className="text-sm font-medium text-[#1f222d]">Web Sitesi</label>
                <input data-testid="company-website" value={company.website} onChange={(e) => setCompany((c) => ({ ...c, website: e.target.value }))} className="w-full mt-1.5 border border-slate-200 rounded-xl px-4 py-2.5 outline-none focus:border-blue-400 text-[#1f222d]" placeholder="https://" />
              </div>
              <div className="sm:col-span-2">
                <label className="text-sm font-medium text-[#1f222d]">Hakkında</label>
                <textarea data-testid="company-about" value={company.about} onChange={(e) => setCompany((c) => ({ ...c, about: e.target.value }))} rows={3} className="w-full mt-1.5 border border-slate-200 rounded-xl px-4 py-2.5 outline-none focus:border-blue-400 text-[#1f222d] resize-none" />
              </div>
            </div>
            <Button data-testid="company-save" onClick={saveCompany} className="mt-6 bg-blue-600 hover:bg-blue-700 text-white rounded-xl px-8 py-5 font-semibold">Kaydet</Button>
          </div>
        )}

        {tab === "applications" && (
          <div className="mt-6">
            <h2 className="text-xl font-bold text-[#1f222d] mb-5">Gelen Başvurular ({apps.length})</h2>
            {apps.length === 0 ? (
              <div className="text-center py-16 bg-slate-50 rounded-2xl border border-slate-100">
                <FileText size={36} className="text-slate-300 mx-auto mb-3" />
                <p className="text-[#1f222d] font-semibold">Henüz başvuru yok</p>
                <p className="text-sm text-slate-500 mt-1">İlanlarınıza başvurular geldiğinde burada görünür.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {apps.map((a) => {
                  const st = { pending: ["Bekliyor", "bg-amber-50 text-amber-700 border-amber-200"], approved: ["Onaylandı", "bg-green-50 text-green-700 border-green-200"], rejected: ["Reddedildi", "bg-red-50 text-red-700 border-red-200"] }[a.status] || ["Bekliyor", ""];
                  return (
                    <div key={a.id} data-testid={`emp-app-${a.id}`} className="bg-white border border-slate-200 rounded-2xl p-5">
                      <div className="flex items-start justify-between gap-4 flex-wrap">
                        <div className="min-w-0">
                          <h3 className="font-semibold text-[#1f222d]">{a.seeker_name} {a.seeker_title && <span className="text-slate-400 font-normal">· {a.seeker_title}</span>}</h3>
                          <p className="text-sm text-slate-500 mt-0.5">Başvurduğu ilan: <span className="font-medium text-[#1f222d]">{a.job_title}</span></p>
                          <div className="flex flex-wrap gap-4 mt-3 text-sm">
                            <a href={`tel:${a.seeker_phone}`} className="inline-flex items-center gap-1.5 text-blue-600 hover:underline"><Phone size={15} /> {a.seeker_phone}</a>
                            <a href={`mailto:${a.seeker_email}`} className="inline-flex items-center gap-1.5 text-slate-600 hover:text-blue-600"><Mail size={15} /> {a.seeker_email}</a>
                            {a.cv_file_id ? (
                              <a href={`${API}/files/${a.cv_file_id}`} target="_blank" rel="noreferrer" data-testid={`emp-cv-${a.id}`} className="inline-flex items-center gap-1.5 text-blue-600 hover:underline font-medium"><Download size={15} /> CV İndir</a>
                            ) : (
                              <span className="text-slate-400 text-xs">CV yüklenmemiş</span>
                            )}
                          </div>
                        </div>
                        <span className={`text-sm font-medium rounded-full px-3 py-1 border shrink-0 ${st[1]}`}>{st[0]}</span>
                      </div>
                      {a.status === "pending" && (
                        <div className="flex gap-2 mt-4 pt-4 border-t border-slate-100">
                          <Button data-testid={`emp-approve-${a.id}`} onClick={() => setAppStatus(a.id, "approved")} className="bg-green-600 hover:bg-green-700 text-white rounded-full text-sm px-5"><Check size={15} className="mr-1" /> Onayla</Button>
                          <Button data-testid={`emp-reject-${a.id}`} onClick={() => setAppStatus(a.id, "rejected")} variant="outline" className="rounded-full text-sm px-5 border-red-200 text-red-600 hover:bg-red-50"><X size={15} className="mr-1" /> Reddet</Button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
